/* Responsibility: Own app-facing persistence capabilities behind one semantic service boundary.
Scope: Exposes save/gallery/backup and persistence-mode operations without leaking engine persistence mechanics to UI and store callers. */
import * as gameStore from '../game-store.js'
import { authStore } from '../ui/stores/auth-store.js'
import type { PersistenceMode, SaveMeta } from '../engine/persist.js'
import type { WorldGalleryEntry, VesselGalleryEntry } from "@anyquest/shared-core"

let persistRuntimePromise: Promise<typeof import('../engine/persist.js')> | null = null

function loadPersistRuntime() {
  if (!persistRuntimePromise) persistRuntimePromise = import('../engine/persist.js')
  return persistRuntimePromise
}

function currentUid(): string {
  return authStore.user?.uid ?? ''
}

export const persistenceService = {
  listSaves(): Promise<SaveMeta[]> {
    return gameStore.getSaveList()
  },

  listWorlds(): Promise<WorldGalleryEntry[]> {
    return gameStore.getWorldGallery()
  },

  listVessels(): Promise<VesselGalleryEntry[]> {
    return gameStore.getVesselGallery()
  },

  restoreSave(saveId: string): Promise<void> {
    return gameStore.loadGameById(currentUid(), saveId).then(() => undefined)
  },

  deleteSave(saveId: string): Promise<void> {
    return gameStore.deleteGame(saveId)
  },

  exportActiveBackup(): Promise<string | null> {
    return gameStore.exportActiveSaveBackup()
  },

  importBackup(raw: string): Promise<string | null> {
    return gameStore.importSaveBackupJson(raw)
  },

  async getMode(): Promise<PersistenceMode> {
    const { getPersistenceMode } = await loadPersistRuntime()
    return getPersistenceMode()
  },

  async setMode(mode: PersistenceMode): Promise<PersistenceMode> {
    const { getPersistenceMode, setPersistenceMode } = await loadPersistRuntime()
    setPersistenceMode(mode)
    return getPersistenceMode()
  },

  async startUserSync(uid: string): Promise<void> {
    if (!uid) return
    const { startPendingSaveSync } = await loadPersistRuntime()
    startPendingSaveSync(uid)
  },

  async stopUserSync(uid: string): Promise<void> {
    if (!uid) return
    const { stopPendingSaveSync } = await loadPersistRuntime()
    stopPendingSaveSync(uid)
  },

  async migrateGuestSaves(oldUid: string, newUid: string): Promise<void> {
    if (!oldUid || !newUid) return
    const { migrateGuestSaves } = await loadPersistRuntime()
    await migrateGuestSaves(oldUid, newUid)
  },

  async getSyncCounts(uid: string): Promise<{ pendingSaveCount: number, conflictBackupCount: number }> {
    if (!uid) return { pendingSaveCount: 0, conflictBackupCount: 0 }
    const { getPendingSaveCount, getSaveConflictBackupCount } = await loadPersistRuntime()
    return {
      pendingSaveCount: await getPendingSaveCount(uid),
      conflictBackupCount: await getSaveConflictBackupCount(uid),
    }
  },

  async flushPending(uid: string): Promise<void> {
    if (!uid) return
    const { forceFlushPendingSaves } = await loadPersistRuntime()
    await forceFlushPendingSaves(uid)
  },

  async clearPending(uid: string): Promise<void> {
    if (!uid) return
    const { clearPendingSaves } = await loadPersistRuntime()
    await clearPendingSaves(uid)
  },

  async clearConflictBackups(uid: string): Promise<void> {
    if (!uid) return
    const { clearSaveConflictBackups } = await loadPersistRuntime()
    await clearSaveConflictBackups(uid)
  },
}
